import { getClassOptionLabel } from "../../utils/classPresentation";
import ClassPresentation from "../../Components/ClassPresentation";
import React, { useEffect, useState } from "react";
import { FiPlus, FiUsers } from "react-icons/fi";
import MenuLateral from "../../Components/MenuLateral";
import CadastroPageHeader, { CadastroPanelHeading } from "../../Components/CadastroPageHeader";
import api from "../../services/api";
import { showApiError } from "../../services/showApiError";
import Swal from "sweetalert2";
import FieldLabel from "../../Components/FieldLabel";
import "./index.css";
import "../../styles/cadastro-pages.css";

function CadClasse() {
  // ----- estado formulário/edição -----
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [graduationYear, setGraduationYear] = useState("");
  const [acronym, setAcronym] = useState("");
  const [semester, setSemester] = useState("");
  const [room, setRoom] = useState("");
  const [displayName, setDisplayName] = useState("");

  // ----- instituições -----
  const [instituicoes, setInstituicoes] = useState([]);
  const [isInstituicoesModalOpen, setIsInstituicoesModalOpen] = useState(false);
  const [instituicaoQuery, setInstituicaoQuery] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState(null);

  // ----- turmas / grid -----
  const [lista, setLista] = useState([]);
  const [gridSearch, setGridSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // ----- carregar instituições (para modal) -----
  const carregarInstituicoes = async () => {
    try {
      const resp = await api.get("/institution");
      setInstituicoes(resp.data.institutions || []);
    } catch (err) {
      console.error("Erro ao carregar instituições:", err);
      showApiError(err);
    }
  };

  // ----- carregar turmas (filtradas pela instituição selecionada) -----
  const carregarTurmas = async () => {
    if (!selectedInstitution) {
      setLista([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await api.get("/class", {
        params: { institutionId: selectedInstitution.id },
      });
      // seu controller retorna { classes: [...] }
      setLista(resp.data.classes || resp.data || []);
    } catch (err) {
      console.error("Erro ao carregar turmas:", err);
      showApiError(err, "Erro ao carregar turmas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Carrega instituições para o modal (quando abrir a tela)
    carregarInstituicoes();
    // Esta carga ocorre apenas na montagem da pagina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sempre que a instituição selecionada mudar, carrega as turmas
  useEffect(() => {
    carregarTurmas();
    // A instituicao selecionada e o gatilho completo desta carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitution]);

  // ----- limpar formulário -----
  const limpar = () => {
    setId("");
    setName("");
    setGraduationYear("");
    setAcronym("");
    setSemester("");
    setRoom("");
    setDisplayName("");
  };

  // ----- salvar (POST ou PUT) -----
  const salvar = async () => {
    if (!selectedInstitution) {
      Swal.fire({ icon: "warning", title: "Selecione uma instituição", text: "Antes de salvar, selecione a instituição." });
      return;
    }

    // Validações simples
    if (!name || !name.trim()) {
      Swal.fire({ icon: "warning", title: "Nome obrigatório", text: "Informe o nome da turma." });
      return;
    }

    if (!displayName.trim() || displayName.length > 200) {
      Swal.fire({ icon: "warning", title: "Nome de exibição obrigatório", text: "Informe o nome da turma para exibição com até 200 caracteres." });
      return;
    }

    if (!graduationYear || isNaN(Number(graduationYear))) {
      Swal.fire({ icon: "warning", title: "Ano inválido", text: "Informe um ano de graduação válido (número)." });
      return;
    }

    const payload = {
      name: name.trim(),
      graduationYear: Number(graduationYear),
      institutionId: selectedInstitution.id,
      acronym,
      semester,
      room,
      displayName,
    };

    setLoading(true);
    try {
      if (id) {
        const resp = await api.put(`/class/${id}`, payload);
        const msg = resp.data?.message || "Turma atualizada com sucesso!";
        Swal.fire({ icon: "success", title: "Atualizado", text: msg });
      } else {
        const resp = await api.post("/class", payload);
        const msg = resp.data?.message || "Turma cadastrada com sucesso!";
        Swal.fire({ icon: "success", title: "Cadastrado", text: msg });
      }
      limpar();
      carregarTurmas();
    } catch (err) {
      console.error("Erro ao salvar turma:", err);
      showApiError(err, "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  // ----- selecionar turma do grid para editar -----
  const selecionar = (item) => {
    setId(item.id);
    setName(item.name || "");
    setGraduationYear(item.graduationYear ? String(item.graduationYear) : "");
    setAcronym(item.acronym || "");
    setSemester(item.semester || "");
    setRoom(item.room || "");
    setDisplayName(item.displayName || "");
    const el = document.querySelector(".form-cad");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ----- excluir -----
  const excluir = async (idToDelete) => {
    const confirm = await Swal.fire({
      title: "Confirma exclusão?",
      text: "Deseja realmente excluir esta turma?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const resp = await api.delete(`/class/${idToDelete}`);
      const msg = resp.data?.message || "Turma excluída!";
      Swal.fire({ icon: "success", title: "Excluído", text: msg });
      if (id === idToDelete) limpar();
      carregarTurmas();
    } catch (err) {
      console.error("Erro ao excluir turma:", err);
      showApiError(err, "Erro ao excluir");
    } finally {
      setLoading(false);
    }
  };

  // ----- modal: filtrar e selecionar instituição -----
  const instituicoesFiltradas = instituicoes.filter((it) => {
    const q = instituicaoQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (it.name || "").toLowerCase().includes(q) ||
      (it.city || "").toLowerCase().includes(q) ||
      (it.uf || "").toLowerCase().includes(q)
    );
  });

  const abrirModalInstituicoes = () => {
    setInstituicaoQuery("");
    setIsInstituicoesModalOpen(true);
    carregarInstituicoes();
  };

  const selecionarInstituicao = (it) => {
    setSelectedInstitution(it);
    setIsInstituicoesModalOpen(false);
    setGridSearch("");
    limpar();
  };

  const listaFiltrada = lista.filter((item) =>
    getClassOptionLabel(item).toLowerCase().includes(gridSearch.trim().toLowerCase()),
  );

  return (
    <div className="pagina-cad cadastro-shell cadastro-shell-turmas">
      <MenuLateral />

      <div className="conteudo-cad">
        <CadastroPageHeader
          eyebrow="Cadastros / Estrutura"
          title="Turmas"
          description="Defina a estrutura acadêmica que organiza alunos, eventos e convites de cadastro."
          icon={FiUsers}
          context="Contexto selecionado"
          contextDetail="Escolha uma instituição para trabalhar"
        />

        <div className="selecao-cards">
          <div className={`card-selecao ${selectedInstitution ? "ativo" : ""}`}>
            <div className="card-info">
              <span className="card-label">Instituição</span>

              <strong>
                {selectedInstitution?.name || "Nenhuma selecionada"}
              </strong>

              {selectedInstitution && (
                <small>
                  {selectedInstitution.city} - {selectedInstitution.uf}
                </small>
              )}
            </div>

            <button onClick={abrirModalInstituicoes}>
              {selectedInstitution ? "Trocar" : "Selecionar"}
            </button>
          </div>
        </div>

        {/* FORMULÁRIO */}
        <div className="form-cad">
          <CadastroPanelHeading
            eyebrow={id ? "Edição de cadastro" : "Novo cadastro"}
            title={id ? "Atualizar turma" : "Adicionar turma"}
            description="Os campos ficam disponíveis depois que uma instituição é escolhida."
            icon={id ? FiUsers : FiPlus}
          />
          <FieldLabel label="Nome cadastral da turma">
            <input
              type="text"
              placeholder="Nome da turma"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <FieldLabel label="Sigla">
            <input
              type="text"
              placeholder="Sigla"
              value={acronym}
              onChange={(e) => setAcronym(e.target.value.toUpperCase())}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <FieldLabel label="Semestre">
            <input
              type="text"
              placeholder="Semestre"
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <FieldLabel label="Sala">
            <input
              type="text"
              placeholder="Sala"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <FieldLabel label="Nome da turma para exibição">
            <input
              type="text"
              placeholder="Nome da turma para exibição"
              required
              maxLength={200}
              aria-required="true"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <FieldLabel label="Ano de graduação">
            <input
              type="number"
              placeholder="Ano de graduação (ex: 2026)"
              value={graduationYear}
              onChange={(e) => setGraduationYear(e.target.value)}
              disabled={loading || !selectedInstitution}
            />
          </FieldLabel>

          <div className="botoes">
            <button onClick={salvar} disabled={loading || !selectedInstitution}>
              {id ? "Atualizar" : "Salvar"}
            </button>

            <button className="btn-limpar" onClick={limpar} disabled={loading}>
              Limpar
            </button>
          </div>
        </div>

        {/* GRID */}
        <section className="cadastro-list-panel" aria-labelledby="turmas-list-title">
          <div className="cadastro-list-heading">
            <div>
              <span className="cadastro-list-kicker">Base acadêmica</span>
              <h2 id="turmas-list-title">Turmas cadastradas</h2>
              <p>Consulte e atualize as turmas da instituição selecionada.</p>
            </div>
            <span className="cadastro-list-count">{listaFiltrada.length} {listaFiltrada.length === 1 ? "item" : "itens"}</span>
          </div>

          <div className="grid-toolbar">
            <FieldLabel label="Buscar por nome">
              <input
                className="grid-search-input"
                type="text"
                placeholder="Buscar por nome"
                value={gridSearch}
                onChange={(e) => setGridSearch(e.target.value)}
                disabled={!selectedInstitution}
              />
            </FieldLabel>
          </div>

          {loading && <p className="cadastro-loading" role="status">Carregando turmas...</p>}

          <div className="cadastro-table-wrap">
          <table className="grid-cad">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Sigla</th>
              <th>Ano Grad.</th>
              <th width="200">Ações</th>
            </tr>
          </thead>

          <tbody>
            {listaFiltrada.map((item) => (
              <tr key={item.id}>
                <td><ClassPresentation schoolClass={item} /></td>
                <td>{item.acronym || "-"}</td>
                <td>{item.graduationYear ?? ""}</td>
                <td className="grid-actions">
                  <button onClick={() => selecionar(item)} disabled={loading}>
                    Editar
                  </button>
                  <button className="btn-excluir" onClick={() => excluir(item.id)} disabled={loading}>
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
            {listaFiltrada.length === 0 && !loading && (
              <tr>
                <td colSpan="5">Nenhuma turma encontrada para a instituição selecionada.</td>
              </tr>
            )}
          </tbody>
          </table>
          </div>
        </section>
      </div>

      {/* ----- MODAL INSTITUIÇÕES ----- */}
      {isInstituicoesModalOpen && (
        <div className="modal-overlay" onClick={() => setIsInstituicoesModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Selecione a Instituição</h3>
              <button className="modal-close" onClick={() => setIsInstituicoesModalOpen(false)}>×</button>
            </div>

            <div className="modal-body">
              <FieldLabel label="Buscar instituição">
                <input
                  type="text"
                  placeholder="Buscar instituição (nome / cidade / uf)"
                  value={instituicaoQuery}
                  onChange={(e) => setInstituicaoQuery(e.target.value)}
                  className="modal-search"
                />
              </FieldLabel>

              <div className="modal-list">
                {instituicoesFiltradas.map((it) => (
                  <div key={it.id} className="modal-item" onClick={() => selecionarInstituicao(it)}>
                    <div className="modal-item-left">
                      <strong>{it.name}</strong>
                      <small>{it.city} - {it.uf}</small>
                    </div>
                    <div className="modal-item-right">
                      <button className="btn-select">Selecionar</button>
                    </div>
                  </div>
                ))}

                {instituicoesFiltradas.length === 0 && (
                  <div className="modal-empty">Nenhuma instituição encontrada.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CadClasse;
